import { useState, useRef, useEffect } from "react"
import { useTranslation } from "react-i18next"
import {
  Mail,
  Star,
  Paperclip,
  Search,
  RefreshCw,
  Inbox,
  Send,
  Archive,
  Trash2,
  Link2,
  Plus,
  Reply,
  ReplyAll,
  Forward,
  X,
  FileEdit,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List as ListIcon,
  ListOrdered,
  Minus,
  FileText,
  ChevronDown,
  Eye,
  MousePointerClick,
} from "lucide-react"
import {
  useEmails,
  useEmailThread,
  useMarkEmailRead,
  useToggleEmailStar,
  useComposeEmail,
  useSendEmail,
  useReplyEmail,
  useForwardEmail,
  useAddEmailLink,
  useRemoveEmailLink,
  useEntitySearch,
  type EmailMessage,
  type EntitySearchResult,
} from "@/hooks/use-emails"
import { useEmailAccounts } from "@/hooks/use-email-accounts"
import {
  useEmailTemplates,
  useEmailTemplateUse,
  type EmailTemplate,
} from "@/hooks/use-email-templates"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { formatDate } from "@/lib/utils"

// ── Types ────────────────────────────────────────────────────────

type ComposeMode = "new" | "reply" | "reply_all" | "forward"

interface ComposeState {
  open: boolean
  mode: ComposeMode
  replyToEmail?: EmailMessage
  accountId: string
  to: string[]
  cc: string[]
  bcc: string[]
  subject: string
  bodyHtml: string
  showCc: boolean
  showBcc: boolean
  minimized: boolean
}

const INITIAL_COMPOSE: ComposeState = {
  open: false,
  mode: "new",
  accountId: "",
  to: [],
  cc: [],
  bcc: [],
  subject: "",
  bodyHtml: "",
  showCc: false,
  showBcc: false,
  minimized: false,
}

// ── Folder List ──────────────────────────────────────────────────

const FOLDERS = [
  { key: "INBOX", label: "Odebrane", icon: Inbox },
  { key: "Sent", label: "Wysłane", icon: Send },
  { key: "Drafts", label: "Szkice", icon: FileEdit },
  { key: "Archive", label: "Archiwum", icon: Archive },
  { key: "Trash", label: "Kosz", icon: Trash2 },
]

// ── Recipient Input ─────────────────────────────────────────────

function RecipientInput({
  label,
  value,
  onChange,
}: {
  label: string
  value: string[]
  onChange: (v: string[]) => void
}) {
  const [input, setInput] = useState("")

  const addEmail = (email: string) => {
    const trimmed = email.trim().toLowerCase()
    if (trimmed && trimmed.includes("@") && !value.includes(trimmed)) {
      onChange([...value, trimmed])
    }
    setInput("")
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      e.preventDefault()
      if (input.trim()) addEmail(input)
    } else if (e.key === "Backspace" && !input && value.length > 0) {
      onChange(value.slice(0, -1))
    }
  }

  const handleBlur = () => {
    if (input.trim()) addEmail(input)
  }

  return (
    <div className="flex items-start gap-2">
      <span
        className="w-7 flex-shrink-0 pt-1.5 text-xs font-medium text-muted-foreground"
      >
        {label}:
      </span>
      <div
        className="flex min-h-8 flex-1 flex-wrap items-center gap-1 rounded border px-2 py-1 bg-muted"
      >
        {value.map((email) => (
          <span
            key={email}
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs bg-primary/10 text-primary"
          >
            {email}
            <button
              onClick={() => onChange(value.filter((e) => e !== email))}
              className="ml-0.5 opacity-60 hover:opacity-100"
            >
              <X size={10} />
            </button>
          </span>
        ))}
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={value.length === 0 ? "Wpisz adres email..." : ""}
          className="min-w-[120px] flex-1 border-none bg-transparent py-0.5 text-xs outline-none text-foreground"
        />
      </div>
    </div>
  )
}

// ── Rich Text Editor (contentEditable) ──────────────────────────

function RichTextEditor({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (html: string) => void
  placeholder?: string
}) {
  const editorRef = useRef<HTMLDivElement>(null)
  const internalValue = useRef(value)

  // Sync external value changes (e.g. template insertion) into contentEditable
  useEffect(() => {
    if (editorRef.current && value !== internalValue.current) {
      editorRef.current.innerHTML = value
      internalValue.current = value
    }
  }, [value])

  const execCommand = (command: string, val?: string) => {
    document.execCommand(command, false, val)
    editorRef.current?.focus()
    if (editorRef.current) {
      internalValue.current = editorRef.current.innerHTML
      onChange(editorRef.current.innerHTML)
    }
  }

  const handleInput = () => {
    if (editorRef.current) {
      internalValue.current = editorRef.current.innerHTML
      onChange(editorRef.current.innerHTML)
    }
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 border-b px-2 py-1">
        {[
          { icon: Bold, cmd: "bold", title: "Pogrubienie" },
          { icon: Italic, cmd: "italic", title: "Kursywa" },
          { icon: UnderlineIcon, cmd: "underline", title: "Podkreślenie" },
        ].map(({ icon: Icon, cmd, title }) => (
          <button
            key={cmd}
            onClick={() => execCommand(cmd)}
            className="flex h-6 w-6 items-center justify-center rounded transition-colors hover:opacity-80 text-muted-foreground"
            title={title}
            type="button"
          >
            <Icon size={13} />
          </button>
        ))}
        <div className="mx-1 h-4 w-px bg-border" />
        <button
          onClick={() => execCommand("insertUnorderedList")}
          className="flex h-6 w-6 items-center justify-center rounded transition-colors hover:opacity-80 text-muted-foreground"
          title="Lista"
          type="button"
        >
          <ListIcon size={13} />
        </button>
        <button
          onClick={() => execCommand("insertOrderedList")}
          className="flex h-6 w-6 items-center justify-center rounded transition-colors hover:opacity-80 text-muted-foreground"
          title="Lista numerowana"
          type="button"
        >
          <ListOrdered size={13} />
        </button>
        <div className="mx-1 h-4 w-px bg-border" />
        <button
          onClick={() => execCommand("insertHorizontalRule")}
          className="flex h-6 w-6 items-center justify-center rounded transition-colors hover:opacity-80 text-muted-foreground"
          title="Linia pozioma"
          type="button"
        >
          <Minus size={13} />
        </button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="min-h-[120px] flex-1 overflow-auto p-3 text-sm leading-[1.6] outline-none text-foreground"
        data-placeholder={placeholder}
        dangerouslySetInnerHTML={{ __html: value }}
        suppressContentEditableWarning
      />
    </div>
  )
}

// ── Template Selector ───────────────────────────────────────────

function TemplateSelector({
  onSelect,
}: {
  onSelect: (template: EmailTemplate) => void
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const { data: templatesData } = useEmailTemplates(search.length >= 2 ? { search } : {})
  const templates = templatesData?.data || []
  const templateUseMutation = useEmailTemplateUse()

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1 text-[11px] hover:underline text-primary"
        title="Wstaw szablon"
      >
        <FileText size={12} />
        Szablon
      </button>
    )
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(false)}
        className="flex items-center gap-1 text-[11px] hover:underline text-primary"
      >
        <FileText size={12} />
        Szablon
        <ChevronDown size={10} />
      </button>
      <div className="absolute left-0 bottom-6 z-50 w-72 rounded-md border shadow-lg bg-card">
        <div className="p-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj szablonu..."
            className="w-full rounded border px-2 py-1 text-xs bg-muted text-foreground"
            autoFocus
          />
        </div>
        <div className="max-h-48 overflow-auto">
          {templates.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              {search.length >= 2 ? "Brak wyników" : "Brak szablonów"}
            </p>
          ) : (
            templates.map((t) => (
              <button
                key={t.id}
                onClick={() => {
                  onSelect(t)
                  templateUseMutation.mutate(t.id)
                  setOpen(false)
                  setSearch("")
                }}
                className="flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors hover:opacity-80 text-foreground"
              >
                <span className="flex items-center gap-2 text-xs font-medium">
                  {t.name}
                  {t.category && (
                    <span className="rounded px-1 py-0.5 text-[9px] bg-primary/10 text-primary">
                      {t.category}
                    </span>
                  )}
                </span>
                {t.subject && (
                  <span className="truncate text-[10px] text-muted-foreground">
                    {t.subject}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// ── Email Composer ──────────────────────────────────────────────

function EmailComposer({
  state,
  accounts,
  onClose,
  onToggleMinimize,
  onChange,
  onSend,
  isSending,
}: {
  state: ComposeState
  accounts: Array<{ id: string; email: string; display_name?: string }>
  onClose: () => void
  onToggleMinimize: () => void
  onChange: (updates: Partial<ComposeState>) => void
  onSend: () => void
  isSending: boolean
}) {
  if (!state.open) return null

  const modeLabels: Record<ComposeMode, string> = {
    new: "Nowa wiadomość",
    reply: "Odpowiedź",
    reply_all: "Odpowiedź do wszystkich",
    forward: "Przekaż dalej",
  }

  if (state.minimized) {
    return (
      <div
        className="fixed bottom-0 right-6 z-50 flex w-72 cursor-pointer items-center justify-between rounded-t-lg border border-b-0 px-3 py-2 shadow-lg bg-primary text-primary-foreground"
        onClick={onToggleMinimize}
      >
        <span className="text-sm font-medium">{modeLabels[state.mode]}</span>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
            className="rounded p-0.5 hover:opacity-80"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      className="fixed bottom-0 right-6 z-50 flex max-h-[70vh] w-[560px] flex-col rounded-t-lg border border-b-0 shadow-xl bg-card"
    >
      {/* Title bar */}
      <div className="flex items-center justify-between rounded-t-lg px-3 py-2 bg-primary text-primary-foreground">
        <span className="text-sm font-medium">{modeLabels[state.mode]}</span>
        <div className="flex items-center gap-1">
          <button onClick={onToggleMinimize} className="rounded p-0.5 hover:opacity-80">
            <Minus size={14} />
          </button>
          <button onClick={onClose} className="rounded p-0.5 hover:opacity-80">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* From account */}
      <div className="flex items-center gap-2 border-b px-3 py-1.5">
        <span className="w-7 flex-shrink-0 text-xs font-medium text-muted-foreground">
          Od:
        </span>
        <select
          value={state.accountId}
          onChange={(e) => onChange({ accountId: e.target.value })}
          className="flex-1 border-none bg-transparent text-xs outline-none text-foreground"
        >
          {accounts.length === 0 && <option value="">Brak kont email</option>}
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.display_name ? `${a.display_name} <${a.email}>` : a.email}
            </option>
          ))}
        </select>
      </div>

      {/* Recipients */}
      <div className="space-y-1 border-b px-3 py-1.5">
        <RecipientInput label="Do" value={state.to} onChange={(to) => onChange({ to })} />

        {!state.showCc && !state.showBcc && (
          <div className="flex items-center gap-2 pl-8">
            <button
              onClick={() => onChange({ showCc: true })}
              className="text-[10px] hover:underline text-primary"
            >
              DW
            </button>
            <button
              onClick={() => onChange({ showBcc: true })}
              className="text-[10px] hover:underline text-primary"
            >
              UDW
            </button>
          </div>
        )}

        {state.showCc && (
          <RecipientInput label="DW" value={state.cc} onChange={(cc) => onChange({ cc })} />
        )}
        {state.showBcc && (
          <RecipientInput label="UDW" value={state.bcc} onChange={(bcc) => onChange({ bcc })} />
        )}
      </div>

      {/* Subject */}
      <div className="flex items-center gap-2 border-b px-3 py-1.5">
        <span className="w-7 flex-shrink-0 text-xs font-medium text-muted-foreground">
          Temat:
        </span>
        <input
          value={state.subject}
          onChange={(e) => onChange({ subject: e.target.value })}
          className="flex-1 border-none bg-transparent text-xs outline-none text-foreground"
          placeholder="Temat wiadomości..."
        />
      </div>

      {/* Body editor */}
      <RichTextEditor
        value={state.bodyHtml}
        onChange={(bodyHtml) => onChange({ bodyHtml })}
        placeholder="Napisz wiadomość..."
      />

      {/* Actions */}
      <div className="flex items-center justify-between border-t px-3 py-2">
        <div className="flex items-center gap-3">
          <Button
            onClick={onSend}
            disabled={isSending || state.to.length === 0 || !state.accountId}
            size="sm"
            className="gap-1.5"
          >
            <Send size={13} />
            {isSending ? "Wysyłanie..." : "Wyślij"}
          </Button>
          {state.mode === "new" && (
            <TemplateSelector
              onSelect={(template) => {
                const updates: Partial<ComposeState> = {}
                if (template.subject) updates.subject = template.subject
                if (template.body_html) updates.bodyHtml = template.body_html
                onChange(updates)
              }}
            />
          )}
        </div>
        <button
          onClick={onClose}
          className="text-xs hover:underline text-muted-foreground"
        >
          Odrzuć
        </button>
      </div>
    </div>
  )
}

// ── Email List Item ──────────────────────────────────────────────

function EmailListItem({
  email,
  isSelected,
  onClick,
  onStar,
}: {
  email: EmailMessage
  isSelected: boolean
  onClick: () => void
  onStar: () => void
}) {
  const from = email.from_addresses[0]
  const fromLabel = from?.name || from?.email || "Nieznany"

  return (
    <button
      onClick={onClick}
      className={`flex w-full items-start gap-3 border-b px-3 py-2.5 text-left transition-colors ${isSelected ? "bg-primary/[0.08]" : email.is_read ? "" : "bg-primary/[0.03]"}`}
    >
      {/* Star */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onStar()
        }}
        className={`mt-0.5 flex-shrink-0 ${email.is_starred ? "text-amber-500" : "text-muted-foreground"}`}
      >
        <Star size={14} className={email.is_starred ? "fill-amber-500" : ""} />
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span
            className={`truncate text-sm text-foreground ${email.is_read ? "" : "font-semibold"}`}
          >
            {fromLabel}
          </span>
          <span className="flex-shrink-0 text-[10px] text-muted-foreground">
            {formatDate(email.date)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <p
            className={`truncate text-xs ${email.is_read ? "text-muted-foreground" : "font-medium text-foreground"}`}
          >
            {email.subject || "(brak tematu)"}
          </p>
          {email.has_attachments && (
            <Paperclip size={10} className="flex-shrink-0 text-muted-foreground" />
          )}
        </div>
        {email.body_text && (
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
            {email.body_text.slice(0, 100)}
          </p>
        )}
      </div>

      {!email.is_read && (
        <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
      )}
    </button>
  )
}

// ── Entity Link Manager ──────────────────────────────────────────

function EntityLinkManager({ email }: { email: EmailMessage }) {
  const [showSearch, setShowSearch] = useState(false)
  const [query, setQuery] = useState("")
  const { data: searchResults } = useEntitySearch(query)
  const addLinkMutation = useAddEmailLink()
  const removeLinkMutation = useRemoveEmailLink()

  const results = searchResults?.data || []

  const entityTypeLabels: Record<string, string> = {
    Contact: "Osoba",
    Company: "Firma",
    Deal: "Szansa",
  }

  return (
    <div className="border-t px-4 py-2">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-medium text-muted-foreground">
          Powiązania
        </p>
        <button
          onClick={() => setShowSearch(!showSearch)}
          className="flex items-center gap-1 text-[10px] hover:underline text-primary"
        >
          <Plus size={10} />
          Dodaj
        </button>
      </div>

      {/* Existing links */}
      {email.entity_links && email.entity_links.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1">
          {email.entity_links.map((link) => (
            <span
              key={link.id}
              className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] ${link.link_type === "auto" ? "bg-primary/[0.08] text-primary" : "bg-emerald-500/[0.08] text-emerald-500"}`}
            >
              <Link2 size={8} />
              {entityTypeLabels[link.entity_type] || link.entity_type}: {link.entity_name || link.entity_id}
              <button
                onClick={() => removeLinkMutation.mutate({ emailId: email.id, linkId: link.id })}
                className="ml-0.5 opacity-60 hover:opacity-100"
              >
                <X size={8} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search for new links */}
      {showSearch && (
        <div className="mt-1">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Szukaj osoby, firmy lub szansy..."
            className="w-full rounded border px-2 py-1 text-xs bg-muted text-foreground"
            autoFocus
          />
          {query.length >= 2 && results.length > 0 && (
            <div className="mt-1 max-h-32 overflow-auto rounded border bg-card">
              {results.map((r) => (
                <button
                  key={`${r.entity_type}-${r.entity_id}`}
                  onClick={() => {
                    addLinkMutation.mutate({
                      emailId: email.id,
                      data: { entity_type: r.entity_type, entity_id: r.entity_id },
                    })
                    setQuery("")
                    setShowSearch(false)
                  }}
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs transition-colors hover:opacity-80 text-foreground"
                >
                  <span className="rounded px-1 py-0.5 text-[9px] font-medium bg-primary/10 text-primary">
                    {entityTypeLabels[r.entity_type] || r.entity_type}
                  </span>
                  <span className="truncate">{r.label}</span>
                  {r.sublabel && (
                    <span className="truncate text-[10px] text-muted-foreground">
                      {r.sublabel}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {query.length >= 2 && results.length === 0 && (
            <p className="mt-1 text-[10px] text-muted-foreground">
              Brak wyników
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ── Single Email Message (for thread view) ───────────────────────

function EmailMessageView({
  email,
  isExpanded,
  onToggle,
}: {
  email: EmailMessage
  isExpanded: boolean
  onToggle: () => void
}) {
  const from = email.from_addresses[0]

  return (
    <div className="border-b">
      {/* Collapsed header */}
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:opacity-90"
      >
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-medium bg-primary text-primary-foreground">
          {(from?.name || from?.email || "?")[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium truncate text-foreground">
              {from?.name || from?.email}
            </span>
            <span className="text-[10px] text-muted-foreground">
              {new Date(email.date).toLocaleString("pl-PL")}
            </span>
          </div>
          {!isExpanded && (
            <p className="truncate text-[11px] text-muted-foreground">
              {email.body_text?.slice(0, 80) || ""}
            </p>
          )}
        </div>
        {email.direction === "outbound" && (
          <Send size={10} className="flex-shrink-0 text-muted-foreground" />
        )}
      </button>

      {/* Expanded body */}
      {isExpanded && (
        <div className="px-4 pb-3">
          <div className="ml-10 text-xs text-muted-foreground">
            <span>Do: {email.to_addresses.map((a) => a.name || a.email).join(", ")}</span>
          </div>
          <div className="ml-10 mt-2">
            {email.body_html ? (
              <div
                className="prose prose-sm max-w-none text-foreground"
                dangerouslySetInnerHTML={{ __html: email.body_html }}
              />
            ) : (
              <pre
                className="whitespace-pre-wrap font-[inherit] text-sm text-foreground"
              >
                {email.body_text || ""}
              </pre>
            )}
          </div>
          {email.attachments && email.attachments.length > 0 && (
            <div className="ml-10 mt-2 flex flex-wrap gap-1">
              {email.attachments.map((att) => (
                <span
                  key={att.id}
                  className="flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] text-muted-foreground"
                >
                  <Paperclip size={8} />
                  {att.filename}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Email Detail with Thread View ───────────────────────────────

function EmailDetail({
  email,
  onReply,
  onReplyAll,
  onForward,
}: {
  email: EmailMessage
  onReply: () => void
  onReplyAll: () => void
  onForward: () => void
}) {
  const from = email.from_addresses[0]
  const { data: threadData } = useEmailThread(email.id)
  const threadEmails = threadData?.data || []
  const hasThread = threadEmails.length > 1
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set([email.id]))

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-foreground">
              {email.subject || "(brak tematu)"}
            </h2>
            {hasThread && (
              <Badge variant="secondary" className="text-[10px]">
                {threadEmails.length} wiad.
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={onReply}
              className="flex h-7 w-7 items-center justify-center rounded transition-colors hover:opacity-80 text-muted-foreground"
              title="Odpowiedz"
            >
              <Reply size={15} />
            </button>
            <button
              onClick={onReplyAll}
              className="flex h-7 w-7 items-center justify-center rounded transition-colors hover:opacity-80 text-muted-foreground"
              title="Odpowiedz wszystkim"
            >
              <ReplyAll size={15} />
            </button>
            <button
              onClick={onForward}
              className="flex h-7 w-7 items-center justify-center rounded transition-colors hover:opacity-80 text-muted-foreground"
              title="Przekaż dalej"
            >
              <Forward size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Tracking stats for outbound emails */}
      {email.direction === "outbound" && (email.open_count > 0 || email.click_count > 0) && (
        <div
          className="flex items-center gap-4 border-b bg-emerald-500/[0.04] px-4 py-1.5"
        >
          {email.open_count > 0 && (
            <span className="flex items-center gap-1 text-[11px] text-emerald-500">
              <Eye size={12} />
              Otwarte {email.open_count}x
              {email.opened_at && (
                <span className="text-muted-foreground">
                  — {formatDate(email.opened_at)}
                </span>
              )}
            </span>
          )}
          {email.click_count > 0 && (
            <span className="flex items-center gap-1 text-[11px] text-blue-500">
              <MousePointerClick size={12} />
              Kliknięte {email.click_count}x
            </span>
          )}
        </div>
      )}

      {/* Thread view or single email */}
      <div className="flex-1 overflow-auto">
        {hasThread ? (
          // Thread view: show all emails in conversation
          <div>
            {threadEmails.map((threadEmail) => (
              <EmailMessageView
                key={threadEmail.id}
                email={threadEmail}
                isExpanded={expandedIds.has(threadEmail.id)}
                onToggle={() => toggleExpand(threadEmail.id)}
              />
            ))}
          </div>
        ) : (
          // Single email view
          <>
            <div className="px-4 pt-3">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-medium bg-primary text-primary-foreground">
                  {(from?.name || from?.email || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">
                      {from?.name || from?.email}
                    </span>
                    {from?.name && (
                      <span className="text-xs text-muted-foreground">
                        &lt;{from.email}&gt;
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <span>Do: </span>
                    {email.to_addresses.map((a, i) => (
                      <span key={i}>
                        {i > 0 && ", "}
                        {a.name || a.email}
                      </span>
                    ))}
                  </div>
                  {email.cc_addresses && email.cc_addresses.length > 0 && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <span>DW: </span>
                      {email.cc_addresses.map((a, i) => (
                        <span key={i}>
                          {i > 0 && ", "}
                          {a.name || a.email}
                        </span>
                      ))}
                    </div>
                  )}
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(email.date).toLocaleString("pl-PL")}
                  </span>
                </div>
              </div>
            </div>
            <div className="p-4">
              {email.body_html ? (
                <div
                  className="prose prose-sm max-w-none text-foreground"
                  dangerouslySetInnerHTML={{ __html: email.body_html }}
                />
              ) : (
                <pre
                  className="whitespace-pre-wrap font-[inherit] text-sm text-foreground"
                >
                  {email.body_text || ""}
                </pre>
              )}
            </div>
            {/* Attachments */}
            {email.attachments && email.attachments.length > 0 && (
              <div className="border-t px-4 py-2">
                <p className="mb-1 text-xs font-medium text-muted-foreground">
                  Załączniki ({email.attachments.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {email.attachments.map((att) => (
                    <div
                      key={att.id}
                      className="flex items-center gap-1.5 rounded border px-2 py-1 text-xs text-muted-foreground"
                    >
                      <Paperclip size={10} />
                      <span className="max-w-[150px] truncate">
                        {att.filename}
                      </span>
                      {att.size_bytes && (
                        <span className="text-muted-foreground">
                          ({Math.round(att.size_bytes / 1024)} KB)
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Entity links */}
      <EntityLinkManager email={email} />

      {/* Reply bar */}
      <div className="flex items-center gap-2 border-t px-4 py-2">
        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={onReply}>
          <Reply size={13} />
          Odpowiedz
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={onReplyAll}>
          <ReplyAll size={13} />
          Odpowiedz wszystkim
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={onForward}>
          <Forward size={13} />
          Przekaż dalej
        </Button>
      </div>
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────

export function EmailInboxPage() {
  const { t } = useTranslation()
  const [selectedFolder, setSelectedFolder] = useState("INBOX")
  const [selectedAccountId, setSelectedAccountId] = useState<string>("")
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [compose, setCompose] = useState<ComposeState>(INITIAL_COMPOSE)

  const { data: accountsData } = useEmailAccounts()
  const accounts = accountsData?.data || []

  const { data, isLoading, refetch } = useEmails({
    email_account_id: selectedAccountId || undefined,
    folder: selectedFolder,
    search: searchQuery || undefined,
  })

  const markReadMutation = useMarkEmailRead()
  const starMutation = useToggleEmailStar()
  const composeMutation = useComposeEmail()
  const sendMutation = useSendEmail()
  const replyMutation = useReplyEmail()
  const forwardMutation = useForwardEmail()

  const emails = data?.data || []
  const selectedEmail = emails.find((e) => e.id === selectedEmailId) || null

  const handleSelectEmail = (email: EmailMessage) => {
    setSelectedEmailId(email.id)
    if (!email.is_read) {
      markReadMutation.mutate(email.id)
    }
  }

  // ── Compose Handlers ────────────────────────────────────────

  const defaultAccountId = accounts.length > 0 ? accounts[0].id : ""

  const openNewCompose = () => {
    setCompose({
      ...INITIAL_COMPOSE,
      open: true,
      mode: "new",
      accountId: selectedAccountId || defaultAccountId,
    })
  }

  const openReply = (email: EmailMessage, replyAll: boolean) => {
    const from = email.from_addresses[0]
    const toAddresses = from ? [from.email] : []
    let ccAddresses: string[] = []

    if (replyAll) {
      const allTo = email.to_addresses.map((a) => a.email)
      const allCc = (email.cc_addresses || []).map((a) => a.email)
      ccAddresses = [...allTo, ...allCc].filter(
        (e) => !toAddresses.includes(e) && !accounts.some((a) => a.email === e)
      )
    }

    const subject = email.subject || ""
    const reSubject = subject.startsWith("Re: ") ? subject : `Re: ${subject}`

    const quotedBody = `<br/><br/><div style="border-left: 2px solid #ccc; padding-left: 12px; margin-left: 0; color: #666;">
<p style="margin: 0 0 4px 0; font-size: 12px;">${new Date(email.date).toLocaleString("pl-PL")} ${from?.name || from?.email || ""} napisał(a):</p>
${email.body_html || `<pre>${email.body_text || ""}</pre>`}
</div>`

    setCompose({
      open: true,
      mode: replyAll ? "reply_all" : "reply",
      replyToEmail: email,
      accountId: email.email_account_id || selectedAccountId || defaultAccountId,
      to: toAddresses,
      cc: ccAddresses,
      bcc: [],
      subject: reSubject,
      bodyHtml: quotedBody,
      showCc: ccAddresses.length > 0,
      showBcc: false,
      minimized: false,
    })
  }

  const openForward = (email: EmailMessage) => {
    const subject = email.subject || ""
    const fwdSubject = subject.startsWith("Fwd: ") ? subject : `Fwd: ${subject}`

    const from = email.from_addresses[0]
    const forwardedBody = `<br/><br/><div style="border-top: 1px solid #ccc; padding-top: 12px;">
<p style="margin: 0 0 4px 0; font-size: 12px; color: #666;">---------- Przekazana wiadomość ----------<br/>
Od: ${from?.name || ""} &lt;${from?.email || ""}&gt;<br/>
Data: ${new Date(email.date).toLocaleString("pl-PL")}<br/>
Temat: ${email.subject || "(brak tematu)"}<br/>
Do: ${email.to_addresses.map((a) => a.email).join(", ")}</p>
${email.body_html || `<pre>${email.body_text || ""}</pre>`}
</div>`

    setCompose({
      open: true,
      mode: "forward",
      replyToEmail: email,
      accountId: email.email_account_id || selectedAccountId || defaultAccountId,
      to: [],
      cc: [],
      bcc: [],
      subject: fwdSubject,
      bodyHtml: forwardedBody,
      showCc: false,
      showBcc: false,
      minimized: false,
    })
  }

  const handleSend = async () => {
    if (!compose.accountId || compose.to.length === 0) return

    try {
      if (compose.mode === "reply" || compose.mode === "reply_all") {
        if (compose.replyToEmail) {
          await replyMutation.mutateAsync({
            id: compose.replyToEmail.id,
            data: {
              body_html: compose.bodyHtml,
              reply_all: compose.mode === "reply_all",
              email_account_id: compose.accountId,
            },
          })
        }
      } else if (compose.mode === "forward") {
        if (compose.replyToEmail) {
          await forwardMutation.mutateAsync({
            id: compose.replyToEmail.id,
            data: {
              to: compose.to,
              cc: compose.cc.length > 0 ? compose.cc : undefined,
              body_html: compose.bodyHtml,
              email_account_id: compose.accountId,
            },
          })
        }
      } else {
        // New compose — create then send
        const result = await composeMutation.mutateAsync({
          email_account_id: compose.accountId,
          to: compose.to,
          cc: compose.cc.length > 0 ? compose.cc : undefined,
          bcc: compose.bcc.length > 0 ? compose.bcc : undefined,
          subject: compose.subject,
          body_html: compose.bodyHtml,
          is_draft: false,
        })
        // If the API creates and sends in one step, we're done.
        // Otherwise send the draft:
        if (result?.data?.id) {
          await sendMutation.mutateAsync(result.data.id)
        }
      }
      setCompose(INITIAL_COMPOSE)
    } catch {
      // Error is handled by mutation state
    }
  }

  const isSending =
    composeMutation.isPending || sendMutation.isPending || replyMutation.isPending || forwardMutation.isPending

  return (
    <>
      <div
        className="flex h-[calc(100vh-56px-48px)] rounded-lg border overflow-hidden bg-card"
      >
        {/* Sidebar: Folders + Account selector */}
        <div className="flex w-48 flex-shrink-0 flex-col border-r">
          {/* Compose button */}
          <div className="p-2">
            <Button onClick={openNewCompose} size="sm" className="w-full gap-1.5">
              <Plus size={14} />
              Napisz
            </Button>
          </div>

          {/* Account selector */}
          {accounts.length > 0 && (
            <div className="border-b px-2 pb-2">
              <select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                className="w-full rounded border px-2 py-1 text-xs bg-muted text-foreground"
              >
                <option value="">Wszystkie konta</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.email}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Folder list */}
          <div className="flex-1 py-1">
            {FOLDERS.map((folder) => {
              const Icon = folder.icon
              const isActive = selectedFolder === folder.key
              return (
                <button
                  key={folder.key}
                  onClick={() => setSelectedFolder(folder.key)}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-sm transition-colors ${isActive ? "bg-primary/[0.08] text-primary font-semibold" : "text-muted-foreground"}`}
                >
                  <Icon size={14} />
                  {folder.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Email list */}
        <div className="flex w-80 flex-shrink-0 flex-col border-r">
          {/* Search + Refresh */}
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <div className="relative flex-1">
              <Search
                size={12}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Szukaj..."
                className="w-full rounded border py-1 pl-7 pr-2 text-xs bg-muted text-foreground"
              />
            </div>
            <button
              onClick={() => refetch()}
              className="flex h-6 w-6 items-center justify-center rounded transition-colors hover:opacity-80 text-muted-foreground"
              title="Odśwież"
            >
              <RefreshCw size={12} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-auto">
            {isLoading ? (
              <div className="space-y-2 p-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-16 w-full rounded" />
                ))}
              </div>
            ) : emails.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Mail size={32} className="mb-2 opacity-20 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Brak wiadomości
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {accounts.length === 0
                    ? "Dodaj konto email w ustawieniach"
                    : "Poczta zostanie zsynchronizowana automatycznie"}
                </p>
              </div>
            ) : (
              emails.map((email) => (
                <EmailListItem
                  key={email.id}
                  email={email}
                  isSelected={email.id === selectedEmailId}
                  onClick={() => handleSelectEmail(email)}
                  onStar={() => starMutation.mutate(email.id)}
                />
              ))
            )}
          </div>
        </div>

        {/* Email detail */}
        <div className="flex-1 overflow-hidden">
          {selectedEmail ? (
            <EmailDetail
              email={selectedEmail}
              onReply={() => openReply(selectedEmail, false)}
              onReplyAll={() => openReply(selectedEmail, true)}
              onForward={() => openForward(selectedEmail)}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center">
              <Mail size={48} className="mb-3 opacity-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Wybierz wiadomość, aby zobaczyć szczegóły
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Compose overlay */}
      <EmailComposer
        state={compose}
        accounts={accounts}
        onClose={() => setCompose(INITIAL_COMPOSE)}
        onToggleMinimize={() => setCompose((s) => ({ ...s, minimized: !s.minimized }))}
        onChange={(updates) => setCompose((s) => ({ ...s, ...updates }))}
        onSend={handleSend}
        isSending={isSending}
      />
    </>
  )
}
